import { useState } from "react"
import { useDebounce } from "use-debounce"
import { $Pressable } from "@legendapp/state/react-native"
import { Show, useObservable } from "@legendapp/state/react"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { Pressable, View, Modal, Platform } from "react-native"

import { ProductType } from "@white-labels/input"

import { ar } from "@src/i18n/ar"
import { useNumCols } from "@src/hooks"
import { cn, ENV, priceFromDBFormat, sharedStyles, trpc } from "@src/utils"

import { List } from "./list"
import { Header } from "./Header"
import { Text, TextInput, MCIcon, Image, ActivityIndicator } from "./core"

type Props = {
  value?: number[]
  onChange?: (ids: number[]) => void
  types: ProductType[]
  className?: string
  mode?: "single" | "multiple"
  onlyBaseProducts?: boolean
  maxSelection?: number
}

export function ProductPicker({ value = [], onChange, className, mode = "multiple", types, maxSelection, onlyBaseProducts }: Props) {
  const ids = value
  const visible = useObservable(false)
  const products = trpc.products.getMultiple.useQuery({ ids }, { enabled: ids.length > 0 })
  const displayText = ids.length > 0 ? (products.data?.products.map((p) => p.name).join(", ") ?? ar.loading) : ar.selectProduct

  function hide() {
    visible.set(false)
  }

  function handleChange(ids: number[]) {
    onChange?.(ids)
    if (mode === "single") hide()
  }

  function showPickerModal() {
    visible.set(true)
  }

  return (
    <>
      <Pressable
        className={cn("min-h-[45px] bg-white rounded px-4 justify-center active:opacity-70 flex-row items-center border border-gray-300", className)}
        onPress={showPickerModal}
      >
        <Text className={cn("flex-1", ids.length === 0 && "text-gray-400")} numberOfLines={1}>
          {displayText}
        </Text>
        <View className="absolute left-3">
          <MCIcon name="chevron-down" size={24} color="#999" />
        </View>
      </Pressable>

      <Show if={visible}>
        <ProductPickModal value={value} onChange={handleChange} hide={hide} mode={mode} types={types} maxSelection={maxSelection} onlyBaseProducts={onlyBaseProducts} />
      </Show>
    </>
  )
}

type ModalProps = Omit<Props, "className"> & { hide: () => void }

const TYPE_OPTIONS: { type: ProductType; label: string }[] = [
  { type: "STANDARD_PRODUCT", label: ar.STANDARD_PRODUCT },
  { type: "LOCAL_PRODUCT", label: ar.LOCAL_PRODUCT },
  { type: "WEIGHT_PRODUCT", label: ar.WEIGHT_PRODUCT },
  { type: "WEIGHT_DEFINED_PRODUCT", label: ar.WEIGHT_DEFINED_PRODUCT },
]

export function ProductPickModal({ value = [], onChange, hide, mode = "multiple", types, maxSelection, onlyBaseProducts }: ModalProps & { onlyBaseProducts?: boolean }) {
  const utils = trpc.useUtils()
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 500)
  const [type, setType] = useState<ProductType | "selected" | undefined>(undefined)
  const value$ = useObservable(value)
  const numCols = Math.max(2, useNumCols(380, { maxWidth: 1200 }))
  const insets = useSafeAreaInsets()

  const viewingSelected = type === "selected"
  const activeType = type !== "selected" ? type : undefined

  const products = trpc.products.list.useInfiniteQuery(
    { search: debouncedSearch, types: activeType ? [activeType] : types.length > 0 ? types : undefined, onlyBaseProducts, limit: 50 },
    { getNextPageParam: (lastPage) => lastPage.nextCursor },
  )

  function handleTypeToggle(type: ProductType) {
    setType((prev) => (prev === type ? undefined : type))
  }

  const selectedProducts = trpc.products.getMultiple.useQuery({ ids: value }, { enabled: viewingSelected && value.length > 0 })

  // `list` adds a `rank` field for search ordering that `getMultiple` lacks;
  // both render with the same card, so we strip `rank` to unify the item shape.
  const catalogData = (products.data?.pages.flatMap((page) => page.items) || []).map(({ rank: _rank, ...p }) => p)
  const data = viewingSelected ? (selectedProducts.data?.products ?? []).filter((p) => value$.get().includes(p.id)) : catalogData

  const header = (
    <>
      <Header
        title={`${ar.searchProducts} ${onlyBaseProducts ? `- ${ar.baseProductsOnly}` : ""}`.trim()}
        withoutInsets={Platform.OS === "ios"}
        canGoBack={false}
        right={
          <View className="h-full flex-row">
            {mode === "multiple" && (
              <Pressable
                onPress={() => {
                  onChange?.(value$.get())
                  hide()
                }}
                className="py-2 px-4 h-full"
              >
                <Text className="text-blue-500 font-bold">{ar.done}</Text>
              </Pressable>
            )}
            {mode === "single" && <View className="w-10" />}
            <Pressable onPress={hide} hitSlop={12} className="p-2 h-full">
              <MCIcon name="close" size={24} color="#333" />
            </Pressable>
          </View>
        }
      />
      <View className="w-full max-w-content mx-auto">
        <View className="flex-row flex-wrap gap-1.5 px-3 mb-2">
          {types.map((key) => {
            const option = TYPE_OPTIONS.find((option) => option.type === key)
            if (!option) return null
            const isActive = activeType === option.type
            return (
              <Pressable
                key={option.type}
                onPress={() => handleTypeToggle(option.type)}
                className={cn("px-3 py-1.5 rounded border active:opacity-70", isActive ? "bg-blue-500 border-blue-500" : "bg-white border-gray-200")}
              >
                <Text className={cn("text-sm", isActive ? "text-white" : "text-gray-600")}>{option.label}</Text>
              </Pressable>
            )
          })}
          {mode === "multiple" && value.length > 0 && (
            <Pressable
              onPress={() => setType((prev) => (prev === "selected" ? undefined : "selected"))}
              className={cn("px-3 py-1.5 rounded border active:opacity-70", viewingSelected ? "bg-blue-500 border-blue-500" : "bg-white border-gray-200")}
            >
              <Text className={cn("text-sm", viewingSelected ? "text-white" : "text-gray-600")}>{ar.selectedProducts(value$.get().length)}</Text>
            </Pressable>
          )}
        </View>
        {!viewingSelected && <TextInput autoFocus={false} value={search} onChangeText={setSearch} placeholder={ar.searchByNameOrBrand} className="mx-3" />}
      </View>
    </>
  )

  const ListEmptyComponent = viewingSelected ? (
    selectedProducts.isLoading ? (
      <ActivityIndicator size="small" className="py-8" />
    ) : (
      <Text className="text-center text-gray-500 py-10">{ar.noSelectedProducts}</Text>
    )
  ) : products.isLoading ? (
    <ActivityIndicator size="small" className="py-8" />
  ) : (
    <Text className="text-center text-gray-500 py-10">{search.length > 2 ? ar.noProductsFound : ar.searchProductsToGetStarted}</Text>
  )

  return (
    <Modal animationType="slide" presentationStyle="formSheet" onRequestClose={hide} onDismiss={hide} className="bg-white" allowSwipeDismissal>
      {header}
      <List
        data={data ?? []}
        contentContainerStyle={sharedStyles.contentContainer}
        keyExtractor={(item) => item.id.toString()}
        key={`list-${numCols}`}
        numColumns={numCols}
        onEndReached={() => (!viewingSelected && products.hasNextPage && !products.isFetchingNextPage ? products.fetchNextPage() : null)}
        onEndReachedThreshold={0.7}
        ListEmptyComponent={ListEmptyComponent}
        renderItem={({ item }) => {
          const image = item.images?.at(-1)
          return (
            <$Pressable
              $className={() =>
                cn(
                  "p-2 items-center justify-between active:bg-gray-50 m-1 bg-white shadow-sm rounded border border-gray-100",
                  value$.get().includes(item.id) && "bg-blue-50 border-blue-200",
                )
              }
              onPress={() => {
                utils.products.get.setData({ id: item.id }, (p) => ({
                  ...item,
                  brand: p?.brand ?? null,
                  base: p?.base ?? null,
                  derived: p?.derived ?? [],
                  inventory: p?.inventory ?? null,
                }))
                utils.products.getByUpc.setData({ upc: item.upc }, (p) => ({
                  ...item,
                  brand: p?.brand ?? null,
                  base: p?.base ?? null,
                  derived: p?.derived ?? [],
                  inventory: p?.inventory ?? null,
                }))

                if (mode === "single") {
                  onChange?.([item.id])
                  return
                }

                const current = value$.get()
                const exists = current.includes(item.id)

                if (exists) {
                  value$.set(current.filter((id) => id !== item.id))
                } else {
                  if (maxSelection && current.length >= maxSelection) return
                  value$.set([...current, item.id])
                }
              }}
            >
              <View className="w-full h-[120]">
                <Show if={() => value$.get().includes(item.id)}>
                  <View className="absolute top-1 right-1 z-10 bg-blue-500 rounded pb-0.5 shadow-sm">
                    <MCIcon name="check" size={12} color="white" />
                  </View>
                </Show>
                <Image
                  source={{ uri: image?.key ? `${ENV.R2_URL}/${image?.key}` : undefined }}
                  className={`flex-1 ${image?.key ? "" : "bg-gray-100"}`}
                  contentFit="contain"
                  recyclingKey={image?.key}
                  transition={250}
                  loading="lazy"
                  cachePolicy="memory"
                />
              </View>

              <View className="flex-1 w-full pt-2">
                <Text className="text-sm text-gray-800" numberOfLines={1}>
                  {item.name}
                </Text>
                <Text className="text-xs text-gray-500" numberOfLines={1}>
                  {item.type === "WEIGHT_PRODUCT" ? item.upc.replace(/^20+/, "") : item.upc}
                </Text>
                <View className="flex-row items-center justify-between mt-1">
                  <Text className="text-xs text-gray-500">
                    {priceFromDBFormat(item.price).toFixed(2)} {ar.egp}
                  </Text>
                  {(item.type === "WEIGHT_PRODUCT" || item.type === "WEIGHT_DEFINED_PRODUCT") && (
                    <View className="bg-orange-100 px-1.5 rounded">
                      <Text className="text-[10px] text-orange-700">{item.type === "WEIGHT_PRODUCT" ? ar.weight : ar.weightDefinedProduct}</Text>
                    </View>
                  )}
                </View>
              </View>
            </$Pressable>
          )
        }}
        ListFooterComponent={
          <>
            {products.isFetchingNextPage && <ActivityIndicator size="small" className="py-2" />}
            <View style={{ height: insets.bottom + 20 }} />
          </>
        }
      />
    </Modal>
  )
}
